import validationStyle from '~/components/Input/Validation/Validation.module.css';
import { useRegistrationCategories } from '../hooks/useRegistrationCategories';
import { usePartialUpdateRegistrationCategoryMutation } from '../mutations';
import { CollapseCard } from '~/components/CollapseCard/CollapseCard';
import { ChangeEvent, Fragment, useRef, useState } from 'react';
import { NormalizedRegistrationCategory } from '../queries';
import { Button } from '~/components/Button/Button';
import { Dialog } from '~/components/Dialog/Dialog';
import { Table } from '~/components/Table/Table';
import { Grid } from '~/components/Grid/Grid';
import { Input } from '~/components/Input';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash';

interface QualificationCardProps {
  category: NormalizedRegistrationCategory;
  isOpen: boolean;
  disabled?: boolean;
}

interface RowProps {
  weight: NormalizedRegistrationCategory['weight_classes'][0];
  category: NormalizedRegistrationCategory;
}

function DebouncedWeightInput({ weight }: { weight: RowProps['weight'] }) {
  const partialUpdateMutation = usePartialUpdateRegistrationCategoryMutation();
  const timeoutRef = useRef<number | null>(null);

  const [inputValue, setInputValue] = useState(weight.limit?.toString() ?? '');
  const [error, setError] = useState<string>();

  async function trySubmit(value: string) {
    if (value === '' || Number(value) === weight.limit) {
      return;
    }

    if (Number(value) % 2.5 !== 0) {
      setError('Kvalvikten måste vara en siffra som är jämnt delbar med 2,5 (t.ex. 50, 52,5, 55)');
      return;
    }

    await toast.promise(
      partialUpdateMutation.mutateAsync({
        id: weight.registration_category_id,
        payload: { limit: Number(value) },
      }),
      {
        error: 'Det gick inte att spara, testa igen lite senare.',
        pending: 'Sparar...',
        success: 'Sparat!',
      }
    );
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const value = event.target.value;
    setInputValue(value);
    setError(undefined);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => await trySubmit(value), 500);
  }

  return (
    <Fragment>
      <Input.Field onChange={handleChange} value={inputValue} type="number" step={2.5} />
      {error && <small className={validationStyle.validation}>{error}</small>}
    </Fragment>
  );
}

function Row({ weight }: RowProps) {
  return (
    <Table.Row narrow>
      <Table.Col style={{ width: '50%', verticalAlign: 'top' }}>
        <Input.Field type="text" value={`${weight.name} kg`} disabled />
      </Table.Col>
      <Table.Col style={{ width: '50%' }}>
        <DebouncedWeightInput weight={weight} />
      </Table.Col>
    </Table.Row>
  );
}

export function QualificationCard({ category, isOpen, disabled }: QualificationCardProps) {
  const [showPreFillQualificationDialog, setShowPreFillQualificationDialog] = useState(false);
  const [isPreFillingQualificationDates, setIsPreFillingQualificationDates] = useState(false);

  const partialUpdateMutation = usePartialUpdateRegistrationCategoryMutation();
  const baseId = `${category.competition_type.id}_${category.age_category.id}`;

  const { categories } = useRegistrationCategories();

  async function handlePreFillQualificationDate() {
    setIsPreFillingQualificationDates(true);

    // Exclude current category, since that one is already updated
    for (const cat of categories.filter(c => !isEqual(c, category))) {
      for (const wc of cat.weight_classes) {
        await partialUpdateMutation.mutateAsync({
          id: wc.registration_category_id,
          payload: {
            start_date: category.start_date,
            end_date: category.end_date,
          },
        });
      }
    }

    setIsPreFillingQualificationDates(false);
    setShowPreFillQualificationDialog(false);
  }

  async function handleDateChange(value: string, field: 'start_date' | 'end_date') {
    const promises = category.weight_classes.map(weight =>
      partialUpdateMutation.mutateAsync({
        id: weight.registration_category_id,
        payload: { [field]: value.length > 0 ? value : null },
      })
    );

    const response = await toast.promise(Promise.all(promises), {
      error: 'Det gick inte att spara, testa igen lite senare.',
      pending: 'Sparar...',
      success: 'Sparat!',
    });

    const shouldAskToPreFillQualificationDates =
      // Excluding the current category, since we that would be our "base".
      categories.filter(c => !isEqual(c, category)).every(c => !c.start_date && !c.end_date) &&
      // Ensure that we have at least two categories, since a "bulk update" doesn't make sense on only one category
      categories.length >= 2 &&
      // Ensure that start & end -date is defined
      response.find(n => n)?.start_date &&
      response.find(n => n)?.end_date;

    if (shouldAskToPreFillQualificationDates) {
      setShowPreFillQualificationDialog(true);
    }
  }

  return (
    <Fragment>
      <div style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
        <CollapseCard
          title={`${category.competition_type.description} / ${category.age_category.label} / ${category.is_male ? 'Herr' : 'Dam'}`}
          isOpen={disabled ? false : isOpen}
        >
        <Grid style={{ rowGap: 0 }}>
          <Grid.Item cols={{ sm: 12, md: 6 }}>
            <Input.Group htmlFor={`start_date_${baseId}`} label="Startdatum för kval">
              <Input.Field
                onChange={event => handleDateChange(event.target.value, 'start_date')}
                defaultValue={category.start_date ?? ''}
                id={`start_date_${baseId}`}
                type="date"
              />
            </Input.Group>
          </Grid.Item>

          <Grid.Item cols={{ sm: 12, md: 6 }}>
            <Input.Group htmlFor={`end_date_${baseId}`} label="Slutdatum för kval">
              <Input.Field
                onChange={event => handleDateChange(event.target.value, 'end_date')}
                defaultValue={category.end_date ?? ''}
                id={`end_date_${baseId}`}
                type="date"
              />
            </Input.Group>
          </Grid.Item>
        </Grid>

        <Table bg="even">
          <Table.Head>
            <Table.Row narrow>
              <Table.Col as="th">Vikt</Table.Col>
              <Table.Col as="th">Kvalgräns</Table.Col>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {category.weight_classes.map(weight => (
              <Row key={`${baseId}_${weight.id}`} category={category} weight={weight} />
            ))}
          </Table.Body>
        </Table>
      </CollapseCard>
      </div>

      <Dialog isOpen={showPreFillQualificationDialog} onClose={() => setShowPreFillQualificationDialog(false)}>
        <Dialog.Title>Använda samma kvalperiod?</Dialog.Title>
        <Dialog.Text>Vill du att vi använder samma kvalperiod för alla kategorier för denna tävlingen?</Dialog.Text>

        <Button.Group align="right">
          <Button variant="secondary" onClick={() => setShowPreFillQualificationDialog(false)}>
            Nej
          </Button>
          <Button variant="primary" onClick={handlePreFillQualificationDate} isLoading={isPreFillingQualificationDates}>
            Ja, tack
          </Button>
        </Button.Group>
      </Dialog>
    </Fragment>
  );
}
